import { NgComponentOutlet, PlatformLocation } from '@angular/common';
import { ChangeDetectorRef, Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';

import {
  AuthenticationDialogService,
  PasswordChangeService,
  provideAuthTranslations,
  TwoFaService,
} from '@core/components-authorization-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { injectIsNativeMobileHr } from '@core/tellent-mobile-contracts';
import type { TellentClientDialogResult } from '@core/tellent-types';
import { UserProfileTab } from '@core/tellent-types';
import { resolveMemberInitials } from '@core/tellent-util';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  AvatarComponent,
  DialogBodyComponent,
  DialogHeaderComponent,
  DialogRef,
  TabComponent,
  TabsComponent,
} from '@core/user-interface-legacy';
import { useProtectedControlsCallback } from '@core/user-interface-routing-logic';

import { TELLENT_PROFILE_DIALOG_QUERY_PARAM } from './current-user-profile-tellent-client-route';
import {
  TellentAdminAuthenticationDialogService,
  tellentAuthModuleTranslations,
} from '../services/tellent-admin-authentication-dialog.service';
import { TellentAdminPasswordChangeService } from '../services/tellent-admin-password-change.service';
import { TellentAppTwoFaService } from '../services/tellent-two-fa.service';
import { CurrentUserProfileComponent } from './current-user-profile/current-user-profile.component';
import { CurrentUserProfileCompaniesComponent } from './current-user-profile-companies/current-user-profile-companies.component';
import { CurrentUserProfileSecurityComponent } from './current-user-profile-security/current-user-profile-security.component';
import { CurrentUserAnalyticsService } from './current-user.analytics';

type CurrentUserProfileDialogData = { analyticsContext?: string; tab?: `${UserProfileTab}` };
type CurrentUserProfileDialogResult = TellentClientDialogResult<any>;

@Component({
  selector: 'tlc-current-user-profile-dialog',
  templateUrl: './current-user-profile-dialog.component.html',
  styleUrl: './current-user-profile-dialog.component.less',
  imports: [
    DialogHeaderComponent,
    AvatarComponent,
    TabsComponent,
    TabComponent,
    DialogBodyComponent,
    NgComponentOutlet,
    TranslatePipe,
  ],
  providers: [
    { provide: TwoFaService, useExisting: TellentAppTwoFaService },
    provideAuthTranslations({ useValue: tellentAuthModuleTranslations }),
    { provide: AuthenticationDialogService, useExisting: TellentAdminAuthenticationDialogService },
    { provide: PasswordChangeService, useExisting: TellentAdminPasswordChangeService },
    { provide: TwoFaService, useExisting: TellentAppTwoFaService },
  ],
})
export class CurrentUserProfileDialogComponent
  implements DialogComponentI<CurrentUserProfileDialogData, CurrentUserProfileDialogResult>
{
  #data: CurrentUserProfileDialogData;
  #location = inject(PlatformLocation);
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  protected readonly UserProfileTab = UserProfileTab;
  public readonly isMobile = injectIsNativeMobileHr();
  public webComponentOutput: CurrentUserProfileDialogResult;

  public store = inject(Store);
  public cdRef = inject(ChangeDetectorRef);
  public dialogRef = inject(DialogRef);
  public router = inject(Router);
  public route = inject(ActivatedRoute);
  public analytics = inject(CurrentUserAnalyticsService);

  public currentUser = this.currentUserFacade.currentUser;

  public avatarThumbUrl = computed(() => this.currentUser()!.avatarThumbUrl);

  public initials = computed(() => {
    const { firstName, lastName, email } = this.currentUser()!;
    return resolveMemberInitials({ firstName, lastName, email });
  });

  public fullName = computed(() => {
    const { firstName, lastName } = this.currentUser()!;
    return `${firstName} ${lastName}`;
  });

  public membersCount = computed(() => {
    const members = this.currentUser()?.members;
    return members?.length || '';
  });

  public activeComponent = signal<`${UserProfileTab}`>(UserProfileTab.Profile);
  public componentMap = new Map<`${UserProfileTab}`, any>([
    [UserProfileTab.Profile, CurrentUserProfileComponent],
    [UserProfileTab.Companies, CurrentUserProfileCompaniesComponent],
    [UserProfileTab.Security, CurrentUserProfileSecurityComponent],
  ]);

  private protectedControls = useProtectedControlsCallback();

  public ngAfterViewInit(): void {
    const params = this.route.snapshot.queryParams;
    if (params.tab && !this.isMobile) {
      this.setActiveTab(params.tab);
    }
  }

  public setDialogData(data: CurrentUserProfileDialogData, triggerUpdate: boolean): void {
    this.#data = data;
    this.analytics.trackDialogOpen(data?.analyticsContext);
  }

  public ngOnInit() {
    if (this.#data?.tab) {
      this.activeComponent.set(this.#data.tab);
    }
  }

  public setActiveTab(userProfileTab: UserProfileTab) {
    this.protectedControls(() => {
      this.activeComponent.set(userProfileTab);

      const url = new URL(this.#location.href);
      const currentDialogParam = url.searchParams.get(TELLENT_PROFILE_DIALOG_QUERY_PARAM);
      const [_, context] = currentDialogParam?.split(',') ?? [userProfileTab, 'unknown'];
      url.searchParams.set(TELLENT_PROFILE_DIALOG_QUERY_PARAM, `${userProfileTab},${context}`);

      this.#location.replaceState(undefined, '', url.toString());
    }).subscribe();
  }
}
